import { useEffect, useState } from "react";
import { SignedIn, SignedOut, useClerk, useSignIn, useSignUp } from "@clerk/clerk-react";
import { Navigate, Link, useSearchParams } from "react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { afterAuthPath } from "../pending-invite";

/**
 * 로그인·회원가입 화면. **Clerk의 `<SignIn>`·`<SignUp>`을 쓰지 않고 직접 그린다.**
 *
 * 왜: 그 컴포넌트들을 쓰려면 Clerk을 UI 포함 변형으로 불러야 하는데, 그러면
 * 로그인된 사용자가 여는 **모든 화면**이 UI 묶음까지 받는다. 느린 휴대폰
 * (Fast 3G + CPU 4배)에서 실측하니 `ui-common` 1.25초 + `framework` 0.78초 +
 * `vendors` 0.84초 = **약 2.9초**가 첫 화면 앞에 붙었다. 헤더의 아바타 메뉴만
 * 직접 그려서는 안 없어진다 — 그건 확인했다. `clerkJSVariant="headless"`로
 * 가야 하고, 그러면 이 화면들도 직접 만들어야 한다.
 *
 * 쓰는 건 전부 headless에도 있는 훅이다(useSignIn/useSignUp/useClerk).
 *
 * **`__clerk_ticket`을 여기서 처리한다.** 예전에는 `<SignIn>`이 알아서 받아
 * 줬다. 이 토큰은 벤치·탐침이 임시 계정으로 들어올 때 쓰는 길이라, 빠지면
 * 측정 도구가 통째로 죽는다.
 */
function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 px-4 py-12">
      <Link
        to="/"
        className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden />
        여행가자 소개로 돌아가기
      </Link>
      <div className="flex w-full max-w-sm flex-col gap-5 rounded-xl border bg-card p-6 shadow-sm">
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {children}
      </div>
    </div>
  );
}

/** Clerk 오류에서 사람이 읽을 문장을 꺼낸다. 없으면 일반 문구로 떨어진다. */
function messageOf(err: unknown, fallback: string): string {
  const errors = (err as { errors?: { longMessage?: string; message?: string }[] })?.errors;
  return errors?.[0]?.longMessage ?? errors?.[0]?.message ?? fallback;
}

function ErrorText({ children }: { children: React.ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {children}
    </p>
  );
}

function GoogleButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <Button type="button" variant="outline" className="w-full" onClick={onClick} disabled={disabled}>
      {/* 구글 로고는 규정상 색을 바꾸면 안 된다 — 원본 4색 그대로 둔다. */}
      <svg className="size-4" viewBox="0 0 24 24" aria-hidden>
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
        />
        <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z" />
        <path
          fill="#EA4335"
          d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.05l3.66 2.84c.87-2.6 3.3-4.14 6.16-4.14Z"
        />
      </svg>
      Google로 계속하기
    </Button>
  );
}

const oauthOptions = () => ({
  strategy: "oauth_google" as const,
  redirectUrl: `${window.location.origin}/sso-callback`,
  // 초대로 들어왔다면 초대 화면으로 되돌린다 — 안 그러면 "본인 선택" 단계를
  // 건너뛰고 새 멤버로 들어온다(pending-invite.ts).
  redirectUrlComplete: `${window.location.origin}${afterAuthPath()}`,
});

// ─────────────────────────────────────────────────────────────────────────────

export function SignInPage() {
  return (
    <>
      <SignedIn>
        <Navigate to={afterAuthPath()} replace />
      </SignedIn>
      <SignedOut>
        <SignInForm />
      </SignedOut>
    </>
  );
}

function SignInForm() {
  const { isLoaded, signIn, setActive } = useSignIn();
  const [params] = useSearchParams();
  const ticket = params.get("__clerk_ticket");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<"password" | "second-factor" | "reset-request" | "reset-code">("password");
  const [code, setCode] = useState("");

  // **티켓 로그인.** 초대 메일이나 측정 도구가 이 주소로 들어온다.
  useEffect(() => {
    if (!isLoaded || !signIn || !ticket) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await signIn.create({ strategy: "ticket", ticket });
        if (!cancelled && res.status === "complete") {
          await setActive({ session: res.createdSessionId });
          window.location.replace(afterAuthPath());
        }
      } catch (err) {
        if (!cancelled) setError(messageOf(err, "링크가 만료됐어요. 다시 로그인해주세요"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, signIn, ticket, setActive]);

  if (ticket) {
    return (
      <Shell title="로그인 중…">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          잠시만 기다려주세요
        </div>
        <ErrorText>{error}</ErrorText>
      </Shell>
    );
  }

  async function withBusy(fn: () => Promise<void>, fallback: string) {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(messageOf(err, fallback));
    } finally {
      setBusy(false);
    }
  }

  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    void withBusy(async () => {
      let res = await signIn.create({ identifier: email.trim(), password });
      const first = res.status;
      // **create 한 번으로 안 끝날 수 있다.** 인스턴스 설정에 따라
      // `needs_first_factor`를 돌려주며 비밀번호를 따로 받겠다고 한다(실측).
      if (res.status === "needs_first_factor") {
        res = await signIn.attemptFirstFactor({ strategy: "password", password });
      }
      if (res.status === "complete") {
        await setActive({ session: res.createdSessionId });
        window.location.replace(afterAuthPath());
        return;
      }
      // **이 인스턴스는 비밀번호 다음에 이메일 코드를 한 번 더 받는다**(실측:
      // create가 바로 needs_second_factor + supportedSecondFactors=[email_code]).
      // `<SignIn>`이 대신 해 주던 단계다 — 직접 그리는 이상 우리가 받아야 한다.
      if (res.status === "needs_second_factor") {
        const strategies = res.supportedSecondFactors?.map((f) => f.strategy) ?? [];
        if (strategies.includes("email_code")) {
          await signIn.prepareSecondFactor({ strategy: "email_code" });
          setMode("second-factor");
          return;
        }
        // totp·백업코드는 코드만 입력받으면 되므로 준비 단계가 없다.
        if (strategies.includes("totp") || strategies.includes("backup_code")) {
          setMode("second-factor");
          return;
        }
      }
      setError(`추가 인증이 필요해요 (${first} → ${res.status})`);
    }, "이메일 또는 비밀번호가 올바르지 않아요");
  };

  const submitSecondFactor = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    void withBusy(async () => {
      const strategies = signIn.supportedSecondFactors?.map((f) => f.strategy) ?? [];
      const strategy = strategies.includes("email_code")
        ? ("email_code" as const)
        : strategies.includes("totp")
          ? ("totp" as const)
          : ("backup_code" as const);
      const res = await signIn.attemptSecondFactor({ strategy, code: code.trim() });
      if (res.status === "complete") {
        await setActive({ session: res.createdSessionId });
        window.location.replace(afterAuthPath());
        return;
      }
      setError(`확인을 끝내지 못했어요 (${res.status})`);
    }, "코드가 올바르지 않아요");
  };

  const requestReset = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    void withBusy(async () => {
      await signIn.create({ strategy: "reset_password_email_code", identifier: email.trim() });
      setMode("reset-code");
    }, "재설정 메일을 보내지 못했어요");
  };

  const submitReset = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    void withBusy(async () => {
      const res = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code: code.trim(),
        password,
      });
      if (res.status === "complete") {
        await setActive({ session: res.createdSessionId });
        window.location.replace(afterAuthPath());
        return;
      }
      setError("재설정을 끝내지 못했어요. 다시 시도해주세요");
    }, "코드가 올바르지 않아요");
  };

  if (mode === "second-factor") {
    return (
      <Shell title="한 번 더 확인">
        <form className="flex flex-col gap-4" onSubmit={submitSecondFactor}>
          <p className="text-sm text-muted-foreground">
            보안을 위해 {email}로 보낸 인증 코드를 입력해주세요.
          </p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="second-factor-code">인증 코드</Label>
            <Input
              id="second-factor-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <ErrorText>{error}</ErrorText>
          <Button type="submit" disabled={busy || !isLoaded}>
            {busy ? "확인 중…" : "확인"}
          </Button>
        </form>
        <button
          type="button"
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
          onClick={() => {
            setMode("password");
            setCode("");
            setError("");
          }}
        >
          로그인으로 돌아가기
        </button>
      </Shell>
    );
  }

  if (mode !== "password") {
    return (
      <Shell title="비밀번호 재설정">
        {mode === "reset-request" ? (
          <form className="flex flex-col gap-4" onSubmit={requestReset}>
            <p className="text-sm text-muted-foreground">가입한 이메일로 인증 코드를 보내드려요.</p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reset-email">이메일</Label>
              <Input
                id="reset-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <ErrorText>{error}</ErrorText>
            <Button type="submit" disabled={busy || !isLoaded}>
              {busy ? "보내는 중…" : "코드 받기"}
            </Button>
          </form>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={submitReset}>
            <p className="text-sm text-muted-foreground">{email}로 보낸 코드를 입력해주세요.</p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reset-code">인증 코드</Label>
              <Input
                id="reset-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-password">새 비밀번호</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <ErrorText>{error}</ErrorText>
            <Button type="submit" disabled={busy || !isLoaded}>
              {busy ? "변경 중…" : "비밀번호 변경하고 로그인"}
            </Button>
          </form>
        )}
        <button
          type="button"
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
          onClick={() => {
            setMode("password");
            setError("");
          }}
        >
          로그인으로 돌아가기
        </button>
      </Shell>
    );
  }

  return (
    <Shell title="로그인">
      <GoogleButton
        disabled={!isLoaded || busy}
        onClick={() =>
          void withBusy(
            () => signIn!.authenticateWithRedirect(oauthOptions()),
            "구글 로그인을 시작하지 못했어요"
          )
        }
      />
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        또는
        <span className="h-px flex-1 bg-border" />
      </div>
      <form className="flex flex-col gap-4" onSubmit={submitPassword}>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">이메일</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">비밀번호</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <ErrorText>{error}</ErrorText>
        <Button type="submit" disabled={busy || !isLoaded}>
          {busy ? "로그인 중…" : "로그인"}
        </Button>
      </form>
      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          className="text-muted-foreground underline-offset-4 hover:underline"
          onClick={() => {
            setMode("reset-request");
            setError("");
          }}
        >
          비밀번호를 잊으셨나요?
        </button>
        <Link to="/sign-up" className="font-medium underline-offset-4 hover:underline">
          회원가입
        </Link>
      </div>
    </Shell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * 회원가입은 **Clerk이 호스팅하는 페이지**로 보낸다(accounts.gotravel.day).
 *
 * 왜 우리 화면이 아닌가: `<SignUp>`을 쓰면 Clerk을 UI 포함 변형으로 불러야 하고,
 * 그러면 **로그인된 사용자가 여는 모든 화면**이 UI 묶음까지 받는다. 실측:
 * ui-common 432KB + framework 130KB + vendors 133KB = 705KB, 느린 휴대폰
 * (Fast 3G + CPU 4배)에서 다운로드만 2.9초다. 우리 앱 엔트리 전체가 721KB인데
 * 안 쓰는 UI가 그만큼 붙어 있었다.
 *
 * 직접 만든 폼(SignUpForm, 아래 그대로 남겨 뒀다)은 Clerk의 봇 방어(Turnstile)
 * 위젯이 끝까지 안 풀려 되돌린 적이 있다(2026-09-26). 호스팅 페이지에서는 그게
 * Clerk 쪽 문제라 우리가 안 싸워도 된다.
 *
 * 대가는 가입 한 번에 주소가 바뀌는 것뿐이다 — 사용자당 1회이고, 구글 로그인도
 * 어차피 밖으로 나갔다 온다. 끝나면 `redirect_url`로 되돌아온다.
 */
export function SignUpPage() {
  useEffect(() => {
    const back = `${window.location.origin}${afterAuthPath()}`;
    window.location.replace(
      `https://accounts.gotravel.day/sign-up?redirect_url=${encodeURIComponent(back)}`
    );
  }, []);
  return (
    <>
      <SignedIn>
        <Navigate to={afterAuthPath()} replace />
      </SignedIn>
      <SignedOut>
        <Shell title="회원가입">
          <p className="text-sm text-muted-foreground">가입 화면으로 이동하는 중…</p>
        </Shell>
      </SignedOut>
    </>
  );
}

// 아래는 **지금 쓰지 않는다.** Turnstile 위젯 문제가 풀리면 위 SignUpPage를
// 이걸로 되돌리면 된다(그러면 가입도 우리 도메인에 남는다). 지우지 않는 이유는
// 그때 다시 만들 값어치가 있는 분량이라서다.
function SignUpForm() {
  const { isLoaded, signUp, setActive } = useSignUp();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"form" | "verify">("form");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function withBusy(fn: () => Promise<void>, fallback: string) {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(messageOf(err, fallback));
    } finally {
      setBusy(false);
    }
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;
    void withBusy(async () => {
      const res = await signUp.create({ emailAddress: email.trim(), password });
      if (res.status === "complete") {
        await setActive({ session: res.createdSessionId });
        window.location.replace(afterAuthPath());
        return;
      }
      // **이메일 확인은 건너뛸 수 없다.** 서버(linkClerkIdentity)가 검증된
      // 이메일일 때만 기존 계정에 연결해 준다 — 안 그러면 남의 계정을 가져갈 수 있다.
      if (res.unverifiedFields?.includes("email_address")) {
        await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
        setStep("verify");
        return;
      }
      // 여기까지 오면 인스턴스가 우리가 모르는 값을 더 요구하는 것이다.
      // 조용히 멈추지 말고 무엇이 모자란지 보여준다.
      setError(`가입을 끝내지 못했어요 (${res.status}: ${(res.missingFields ?? []).join(", ") || "알 수 없음"})`);
    }, "가입을 시작하지 못했어요");
  };

  const verify = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;
    void withBusy(async () => {
      const res = await signUp.attemptEmailAddressVerification({ code: code.trim() });
      if (res.status === "complete") {
        await setActive({ session: res.createdSessionId });
        window.location.replace(afterAuthPath());
        return;
      }
      setError("확인을 끝내지 못했어요. 코드를 다시 확인해주세요");
    }, "코드가 올바르지 않아요");
  };

  if (step === "verify") {
    return (
      <Shell title="이메일 확인">
        <form className="flex flex-col gap-4" onSubmit={verify}>
          <p className="text-sm text-muted-foreground">{email}로 보낸 인증 코드를 입력해주세요.</p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="signup-code">인증 코드</Label>
            <Input
              id="signup-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <ErrorText>{error}</ErrorText>
          <Button type="submit" disabled={busy || !isLoaded}>
            {busy ? "확인 중…" : "확인하고 시작하기"}
          </Button>
        </form>
      </Shell>
    );
  }

  return (
    <Shell title="회원가입">
      <GoogleButton
        disabled={!isLoaded || busy}
        onClick={() =>
          void withBusy(
            () => signUp!.authenticateWithRedirect(oauthOptions()),
            "구글 가입을 시작하지 못했어요"
          )
        }
      />
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        또는
        <span className="h-px flex-1 bg-border" />
      </div>
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="signup-email">이메일</Label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="signup-password">비밀번호</Label>
          <Input
            id="signup-password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {/*
          **Clerk 봇 방어가 여기에 붙는다.** 직접 만든 가입 흐름에서는 이 자리를
          우리가 마련해야 한다 — 없으면 "The CAPTCHA failed to load"로 가입이
          통째로 막힌다(실측). `<SignUp>`은 이걸 알아서 그렸다.
        */}
        <div id="clerk-captcha" />
        <ErrorText>{error}</ErrorText>
        <Button type="submit" disabled={busy || !isLoaded}>
          {busy ? "만드는 중…" : "가입하기"}
        </Button>
      </form>
      <p className="text-sm text-muted-foreground">
        이미 계정이 있나요?{" "}
        <Link to="/sign-in" className="font-medium text-foreground underline-offset-4 hover:underline">
          로그인
        </Link>
      </p>
    </Shell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * 구글에서 돌아오는 자리. Clerk이 주소의 상태를 읽어 세션을 만든다.
 * **UI 컴포넌트를 쓰지 않는다** — headless에서도 되는 `handleRedirectCallback`이다.
 */
export function SsoCallbackPage() {
  const { handleRedirectCallback } = useClerk();
  const [error, setError] = useState("");

  useEffect(() => {
    void handleRedirectCallback({ signInFallbackRedirectUrl: afterAuthPath(), signUpFallbackRedirectUrl: afterAuthPath() }).catch(
      (err) => setError(messageOf(err, "로그인을 마치지 못했어요"))
    );
  }, [handleRedirectCallback]);

  return (
    <Shell title={error ? "로그인 실패" : "로그인 중…"}>
      {error ? (
        <>
          <ErrorText>{error}</ErrorText>
          <Link to="/sign-in" className="text-sm underline-offset-4 hover:underline">
            다시 시도하기
          </Link>
        </>
      ) : (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          잠시만 기다려주세요
        </div>
      )}
    </Shell>
  );
}
